// Lazy bbox-bounded "before" snapshot for paint strokes (#78).
//
// A brush stroke touches a bounded region of the layer. Capturing the full
// layer's pre-stroke pixel buffer just to be able to undo the stroke wastes
// memory proportional to layer size, regardless of how few pixels the user
// actually paints.
//
// This helper captures pixels lazily and only for the rows/region the brush
// has signalled it is about to touch. The caller must call `recordBeforeStamp`
// BEFORE each stamp mutation, passing the bbox the stamp will touch. The
// snapshot grows monotonically; previously-captured rows are not re-captured
// when the bbox extends downward or rightward.
//
// Invariant: every pixel in the final composite bbox was captured BEFORE its
// first mutation. The undo step reads `composite()` to get a tight bbox +
// before-pixel buffer suitable for `PaintStrokeCommand`.

import type { PixelBuffer } from "./PixelBuffer";
import type { BBox } from "./brush";

export class LazyBboxSnapshot {
  // The current union of all recorded stamp bboxes, clipped to layer.
  private coverBox: BBox | null = null;
  // Pre-stamp pixel data for the cover box (size = coverBox.w * coverBox.h * 4).
  private buffer: Uint8ClampedArray | null = null;

  constructor(private readonly buf: PixelBuffer) {}

  /**
   * Tell the snapshotter the caller is about to mutate pixels in `stampBox`.
   * The snapshotter captures any pixels in `stampBox` that are not yet in the
   * cover region. Safe to call multiple times before each mutation.
   *
   * `stampBox` is in layer-local coordinates and is clipped to the buffer
   * bounds internally.
   */
  recordBeforeStamp(stampBox: BBox): void {
    const w = this.buf.width;
    const h = this.buf.height;
    // Clip stamp box to layer bounds.
    const sx0 = Math.max(0, Math.floor(stampBox.x));
    const sy0 = Math.max(0, Math.floor(stampBox.y));
    const sx1 = Math.min(w, Math.ceil(stampBox.x + stampBox.w));
    const sy1 = Math.min(h, Math.ceil(stampBox.y + stampBox.h));
    if (sx1 <= sx0 || sy1 <= sy0) return;

    const clipped: BBox = { x: sx0, y: sy0, w: sx1 - sx0, h: sy1 - sy0 };

    if (this.coverBox === null) {
      // First stamp — allocate the buffer for this region.
      this.coverBox = clipped;
      this.buffer = extractRegion(this.buf.data, w, clipped);
      return;
    }

    // Compute the union of the existing cover box and the new stamp box.
    const ux0 = Math.min(this.coverBox.x, clipped.x);
    const uy0 = Math.min(this.coverBox.y, clipped.y);
    const ux1 = Math.max(this.coverBox.x + this.coverBox.w, clipped.x + clipped.w);
    const uy1 = Math.max(this.coverBox.y + this.coverBox.h, clipped.y + clipped.h);
    const unionBox: BBox = { x: ux0, y: uy0, w: ux1 - ux0, h: uy1 - uy0 };

    if (
      unionBox.x === this.coverBox.x &&
      unionBox.y === this.coverBox.y &&
      unionBox.w === this.coverBox.w &&
      unionBox.h === this.coverBox.h
    ) {
      // Stamp is already inside the cover. Nothing to capture.
      return;
    }

    // We need to expand the cover. Allocate a new buffer for the union,
    // copy the old buffer's pixels into their new positions, and capture
    // current pixel data for any region that wasn't previously covered.
    const newBuffer = new Uint8ClampedArray(unionBox.w * unionBox.h * 4);
    // We capture the entire union from the live pixel data, then overwrite
    // the previously-snapshotted region with our stored pre-stamp pixels.
    // Order matters: live data first (which is post-mutation for already-
    // covered cells but pre-mutation for new cells), then old buffer
    // (which is pre-mutation for the previously-covered region) overlays
    // the previously-covered cells.
    copyRegionFromLiveBuffer(this.buf.data, w, unionBox, newBuffer);
    overlayOldBufferIntoNew(this.buffer!, this.coverBox, newBuffer, unionBox);

    this.coverBox = unionBox;
    this.buffer = newBuffer;
  }

  /**
   * The current cover bbox (the union of all stamps recorded so far).
   * `null` if no stamp was recorded.
   */
  get bbox(): BBox | null {
    return this.coverBox;
  }

  /**
   * The pre-stamp pixel buffer for the cover bbox.
   * Size = cover.w * cover.h * 4.
   */
  get pixels(): Uint8ClampedArray | null {
    return this.buffer;
  }

  /**
   * Total bytes currently allocated for the snapshot. Used by tests to
   * verify memory-savings claims.
   */
  get byteLength(): number {
    return this.buffer ? this.buffer.byteLength : 0;
  }
}

function extractRegion(
  data: Uint8ClampedArray,
  width: number,
  bbox: BBox,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * width + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(data.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function copyRegionFromLiveBuffer(
  full: Uint8ClampedArray,
  fullWidth: number,
  bbox: BBox,
  out: Uint8ClampedArray,
): void {
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
}

function overlayOldBufferIntoNew(
  oldBuffer: Uint8ClampedArray,
  oldBox: BBox,
  newBuffer: Uint8ClampedArray,
  newBox: BBox,
): void {
  // Copy each row of `oldBuffer` into `newBuffer` at the correct offset.
  const rowOffsetY = oldBox.y - newBox.y;
  const rowOffsetX = oldBox.x - newBox.x;
  for (let row = 0; row < oldBox.h; row++) {
    const srcOff = row * oldBox.w * 4;
    const dstRow = rowOffsetY + row;
    const dstOff = (dstRow * newBox.w + rowOffsetX) * 4;
    newBuffer.set(oldBuffer.subarray(srcOff, srcOff + oldBox.w * 4), dstOff);
  }
}
